import { useTheme, InputAdornment, TextField, Autocomplete } from "@mui/material"
import React, { useRef, useEffect, useState, useContext } from "react"
import type { FormDataTypeProps, IPoint } from "../../../interfaces"
import DynamicIcon from "../lib/DynamicIcon"
import { BASE_ESRI_API_KEY, BASE_ADDRESS_FUNCTION_APP_API_KEY } from "../../../constants"
import { geolocationService, pointService } from "../../../services"
import SettingsContext from "../../../contexts/settings.context"
import { UPDATE_SETTINGS } from "../../../contexts/reducers/settings.reducer"
import useCurrentLocation from "../../../hooks/useCurrentLocation"

interface AddressComponents {
  address: string
  streetAddress?: string
  city?: string
  region?: string
  postal?: string
  countryCode?: string
  [key: string]: any // Include any additional attributes
}

const AddressSearchInput: React.FC<FormDataTypeProps> = ({ onDataChange, value: propValue, ...props }) => {
  const theme = useTheme()
  if (propValue === undefined) {
    propValue = { inputValue: "" }
  }
  const [inputValue, setInputValue] = React.useState(propValue && propValue.inputValue ? propValue.inputValue : "")
  const [options, setOptions] = React.useState<any[]>([])
  const [selectedCandidate, setSelectedCandidate] = React.useState<AddressComponents | null>(null)
  const [location, setLocation] = React.useState<any[]>([])
  const ref = useRef<HTMLDivElement>(null)
  const [user, setUser] = useState<any>("")
  const { settingsState, updateSettings } = useContext(SettingsContext)
  const { currentLocation, error } = useCurrentLocation()


  // Update inputValue when propValue changes
  useEffect(() => {
    if (propValue.inputValue !== undefined && propValue.inputValue !== inputValue) {
      setInputValue(propValue.inputValue)
    }
  }, [propValue.inputValue])

  useEffect(() => {
    if (currentLocation.latitude && currentLocation.longitude) {
      setLocation([currentLocation.latitude, currentLocation.longitude])
    }

    const data = localStorage.getItem("user")
    if (data) {
      setUser(JSON.parse(data))
    }
  }, [currentLocation.latitude, currentLocation.longitude])

  useEffect(() => {
    if (settingsState.activeFeature?.workTypeId === 3) {
      updateSettings(UPDATE_SETTINGS, {
        ...settingsState,
        activeFeature: {
          ...settingsState.activeFeature,
          pointAdded: false,
        },
      })
    }
  }, [])

  const handleAddPoint = async (data: any) => {
    if (data.candidates[0]) {
      var geoJson: GeoJSON.Point = {
        type: "Point",
        coordinates: [data.candidates[0].location.x, data.candidates[0].location.y],
      }

      const payload: Omit<IPoint, "geoFeature"> = {
        geoJson: JSON.stringify(geoJson),
        deviceId: -1,
        featureId: settingsState.activeFeature?.featureId,
        projectId: settingsState.activeProject?.projectId ?? -1,
        latitude: data.candidates[0].location.y,
        longitude: data.candidates[0].location.x,
      }
      try {
        const res = await pointService.upsertPointByAddressLookup(payload, "geolocation")
        if (res.status === 200) {
          if (settingsState.activeFeature) {
            updateSettings(UPDATE_SETTINGS, {
              ...settingsState,
              activeFeature: {
                ...settingsState.activeFeature,
                pointAdded: true,
              },
            })
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
  }

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      let active = true

      if (inputValue.length < 3) {
        setOptions([])
        return undefined
      }

      const fetchData = async () => {
        try {
          const response = await fetch(
            `https://addresslookupfunction.azurewebsites.net/api/lookup?address=${inputValue}&apikey=${BASE_ADDRESS_FUNCTION_APP_API_KEY}&user=${user?.userName}${location.length > 0 ? `&location=${location[1]},${location[0]}` : ""}`,
          )
          const data = await response.json()
          const formattedData = data.suggestions.map((a: any) => ({
            text: a.label,
            rawData: a.raw,
            service: data.service,
          }))
          if (active) {
            if (data.suggestions) {
              setOptions(formattedData)
            } else {
              setOptions([])
            }
          }
        } catch (error) {
          //If function app is down, fall back to esri
          try {
            console.warn("CALLING OLD ESRI API")
            const esriRetryResposne = await fetch(
              `https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest?text=${encodeURIComponent(
                inputValue,
              )}&f=json&token=${BASE_ESRI_API_KEY}${location.length > 0 ? `&location=${location[1]},${location[0]}` : ""}`,
            )
            const data = await esriRetryResposne.json()
            if (data.suggestions) {
              setOptions(data.suggestions)
            } else {
              setOptions([])
            }
          } catch {
            console.error(error)
            setOptions([])
          }
        }
      }

      fetchData()

      return () => {
        active = false
      }
    }, 750)
    return () => clearTimeout(debounceTimer)
  }, [inputValue])

  const handleChange = async (event: any, newValue: any) => {
    if (typeof newValue === "string") {
      // User typed a value and pressed Enter or blurred the input
      setInputValue(newValue)
      setSelectedCandidate(null)

      onDataChange(props.id, { inputValue: newValue })
    } else if (newValue && newValue.rawData && newValue.rawData.magicKey) {
      // User selected an option from the list AND hit the ESRI api
      try {
        const response = await fetch(
          `https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?magicKey=${encodeURIComponent(
            newValue.rawData.magicKey,
          )}&f=json&outFields=*&token=${BASE_ESRI_API_KEY}`,
        )
        const data = await response.json()
        console.log("findAddressCandidates", data)
        if (data.candidates && data.candidates.length > 0) {
          const candidate = data.candidates[0]
          const attributes = candidate.attributes
          const addressComponents: AddressComponents = {
            service: newValue.service,
            address: candidate.address,
            streetAddress: attributes.Address,
            city: attributes.City,
            region: attributes.Region,
            stateAbreviation: attributes.RegionAbbr,
            postal: attributes.Postal,
            countryCode: attributes.CountryCode,
            ...attributes, // Include any additional attributes if needed
          }
          setSelectedCandidate(addressComponents)
          setInputValue(candidate.address)
          // Pass both the input value and the address components to the parent
          onDataChange(props.id, {
            inputValue: candidate.address,
            addressComponents: addressComponents,
          })

          // if feature is Site Documentation, add point
          if (settingsState.activeFeature?.workTypeId === 3) {
            handleAddPoint(data)
          }
        } else {
          setSelectedCandidate(null)
          setInputValue(newValue.text)
          onDataChange(props.id, { inputValue: newValue.text })
        }
      } catch (error) {
        console.error(error)
        setSelectedCandidate(null)
        setInputValue(newValue.text || "")
        onDataChange(props.id, { inputValue: newValue.text || "" })
      }
    } else if (newValue && newValue.service) {
      // User selected an option from the list
      const addressComponents: AddressComponents = {
        service: newValue.service,
        address: newValue.text,
        streetAddress: newValue.rawData.street_line,
        city: newValue.rawData.city,
        postal: newValue.rawData.zipcode,
        region: newValue.rawData.state,
        stateAbreviation: newValue.rawData.state,
        ...newValue,
      }
      onDataChange(props.id, { inputValue: newValue.text, addressComponents: addressComponents })
    } else {
      // newValue is null or undefined
      setSelectedCandidate(null)
      setInputValue("")
      onDataChange(props.id, { inputValue: "" })
    }
  }

  return (
    <Autocomplete
      ref={ref}
      freeSolo
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue)

        if (reason === "clear") {
          setSelectedCandidate(null)
          onDataChange(props.id, { inputValue: "" })
        } else if (reason === "input" || reason === "reset") {
          setSelectedCandidate(null) // Clear previous selection
          onDataChange(props.id, { inputValue: newInputValue })
        }
      }}
      options={options}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.text || "")}
      renderOption={(propsOption, option) => (
        <li {...propsOption} key={option.magicKey || option.text}>
          {option.text}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={props.disabled}
          sx={{
            "& > .MuiInputBase-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
          variant="outlined"
          fullWidth
          label={props.label}
          name={props.name}
          size="small"
          placeholder={props.placeholder}
          required={props.required}
          helperText={props.hintText}
          InputProps={{
            ...params.InputProps,
            readOnly: props.readOnly,
            required: props.required,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                <InputAdornment position="end">
                  <DynamicIcon name={props.type} color={location.length === 0 ? "red" : ""} />
                </InputAdornment>
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AddressSearchInput
